/**
 * Portal Manta · SumarioOficialPage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { DADOS } from '../../data/lotes-pr.js';
import { M, F, STATUS } from '../../tokens/theme.js';
import { OBRAS_ROD } from '../../globals.js';
import { HeaderArtefato, SubsecaoM, TabelaM, Fonte, CalloutM, WatermarkManta } from '../shared/ArtefatoHelpers.jsx';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { AGENTES_CATALOG } from '../../data/agentes-catalog.js';
import { executarPipeline } from '../agentes/executarPipeline.jsx';
import { API } from '../../app-globals.js';

const SumarioOficialPage = memo(function SumarioOficialPage_Inner(){
  return (<div style={{padding:'0 0 32px 0', fontFamily:F.body}}>
    <HeaderArtefato
      cliente="ANTT-PR (estudo interno)"
      projeto="Análise de Lotes 3·4·5 — Concessão Rodoviária Paraná"
      contrato="Estudo paramétrico Manta v24"
      tipo="Portal técnico"
      versao="24.0"
      data="2026-05-10"
      autor="MN/Manta"
      localizacaoMaster="SharePoint /ANTT_PR/07-relatorios/"
    />

    <div style={{padding:'0 28px'}}>
      <div style={{fontFamily:F.display, fontSize:26, fontWeight:600, color:M.title, marginBottom:8, paddingBottom:10, borderBottom:`2px solid ${M.borderSoft}`, display:'flex', alignItems:'baseline', gap:14}}>
        <span style={{fontFamily:F.mono, fontSize:13, color:'#fff', fontWeight:700, padding:'5px 12px', background:M.accent, letterSpacing:1, flexShrink:0}}>00</span>
        Sumário & Visão Geral
      </div>

      <p style={{fontSize:13.5, color:M.text, lineHeight:1.7, marginTop:14}}>
        Este portal consolida a análise técnica dos <b>3 lotes de concessão rodoviária do Paraná</b> (Lote 3 · BR-376, Lote 4 · PR-323, Lote 5 · BR-369) sob a ótica do orçamento paramétrico Manta confrontado com o orçamento oficial PR Vias.
      </p>

      <SubsecaoM num="00.1" id="sum-arquitetura" titulo="Arquitetura aplicada">
        <p>Este portal segue a <b>Arquitetura Manta · PK_07</b> (modularização em 5 camadas) e os <b>6 Padrões Obrigatórios da Seção 12</b> do manual canônico:</p>
        <TabelaM num="T-00.1" titulo="Padrões aplicados ao portal" headers={['#', 'Padrão', 'Estado neste portal']} rows={[
          ['12.1', 'Numeração rastreável (Subsecao, Tabela, Callout)', '✓ Aplicado'],
          ['12.2', 'Fonte declarada em todo dado', '✓ Aplicado nos dados duros'],
          ['12.3', 'Localização master (SharePoint)', '✓ Declarada no header'],
          ['12.4', 'Versionamento + data + autor', '✓ HeaderArtefato'],
          ['12.5', 'Tour interativo lateral (TourPanel)', '✓ Sticky direita'],
          ['12.6', 'Padrão visual Manta (skill padrao-manta)', '✓ Tokens M oficiais'],
        ]}/>
      </SubsecaoM>

      <SubsecaoM num="00.2" id="sum-mapa" titulo="Mapa do portal · 9 itens em 4 capítulos">
        <TabelaM num="T-00.2" titulo="Estrutura de navegação" headers={['Cap', 'Núm', 'Item', 'O que entrega']} rows={[
          ['⓪',  '00', 'Sumário',                  'Esta página · header + visão geral'],
          ['I',  '01', 'Lote 3 · BR-376',          'Análise técnica completa do lote'],
          ['I',  '02', 'Lote 4 · PR-323',          'Análise técnica completa do lote'],
          ['I',  '03', 'Lote 5 · BR-369',          'Análise técnica completa do lote'],
          ['I',  '04', 'Conhecimento Consolidado', 'KB + KPIs + Orçamento (3 abas)'],
          ['II', '05', 'Novo Estudo',              'Wizard 6 etapas para novo lote'],
          ['II', '06', 'Adicionar à Base',         'Cadastrar caso histórico'],
          ['III','07', 'Critério de Similaridade', 'Tuning do cosseno'],
          ['III','08', 'Catálogo de Agentes',      '10 agentes + 2 pipelines'],
          ['IV', '09', 'Tour & Glossário',         'Navegação guiada + termos'],
        ]}/>
      </SubsecaoM>

      <SubsecaoM num="00.3" id="sum-camadas" titulo="As 5 camadas do PK_07 neste portal">
        <TabelaM num="T-00.3" titulo="Mapeamento das camadas" headers={['#', 'Camada', 'Onde está neste portal']} rows={[
          ['02.1', 'Dados',         'localStorage (manta-kb-extras-v1, manta-orcamentos-gerados-v1, manta-criterio-similar-v1) · DADOS, OBRAS_ROD, CONSOL_FIN inline'],
          ['02.2', 'Backend',       'API Client com fallback offline · /api/agentes/* (validador, leitor, orcamentista) · Claude Sonnet via narrativaAI'],
          ['02.3', 'Frontend',      'React+Vite · single-file · skill padrao-manta aplicada'],
          ['02.4', 'Skills/Agentes','AGENTES_CATALOG (10 agentes em 5 categorias) · funções puras reaproveitáveis'],
          ['02.5', 'Orquestrador',  'executarPipeline() · 2 pipelines (analiseLote, novoOrcamento) · padrão hub-and-spoke'],
        ]}/>
        <CalloutM num="Q-00.1" tipo="info" label="Anti-vazamento (PK_07)">
          O Frontend nunca acessa banco direto, nunca chama LLM direto. Cálculos de similaridade (cosseno) idealmente migrariam para o Backend em produção. Hoje rodam no JSX por simplicidade do MVP.
        </CalloutM>
      </SubsecaoM>

      <SubsecaoM num="00.4" id="sum-padrao-dual" titulo="Padrão dual · Tour + Agente Guia (Seção 13)">
        <p>Este portal implementa o <b>padrão dual obrigatório</b> da Seção 13 do manual:</p>
        <TabelaM num="T-00.4" titulo="Tour vs. Agente Guia" headers={['Aspecto', 'Tour (este painel direito)', 'Agente Guia (item 05 · wizard)']} rows={[
          ['Foco',     'Onde fica cada coisa',     'O que fazer agora'],
          ['Estado',   'Sem estado (navegação)',   'Passo atual + feitos'],
          ['Quando',   'Onboarding, descoberta',   'Durante execução real'],
          ['Conteúdo', 'O que mostra + Por quê',   'Instrução acionável + duração'],
        ]}/>
      </SubsecaoM>

      <WatermarkManta/>
    </div>
  </div>);
});

export { SumarioOficialPage };
export default SumarioOficialPage;
